import React, { useState, useEffect, useContext, useRef } from 'react';
import Layout from './Layout';
import { Users, CalendarCheck, CalendarX, ChevronRight, FileCheck, Camera, Clapperboard, LayoutGrid, List, ExternalLink, QrCode, Share2, PlayCircle, Film, Copy, Check, Download } from 'lucide-react';
import EventContext from '../contexts/EventContext'
import moment from 'moment';
import useApiUrlTranslation from './useApiUrlTranslation';
import 'moment/locale/fr';
import 'moment/locale/it';
import 'moment/locale/en-gb';
import Loader from './Loader';
import { Link, useHistory } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useParams } from 'react-router-dom/cjs/react-router-dom';
import { API_URL } from '../config';
import RegistrationDashboardLayout from './RegistrationDashboardLayout';
import useGetPartenaires from '../customHooks/useGetPartenaires';
import RegistrationDashbordNav from './RegistrationDashbordNav';
import { SendSlotLinkModal } from './RegistrationDashbordCaptationList';

const extractYoutubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|v\/|embed\/|watch\?v=|&v=)([^#&?]{11})/);
    return m ? m[1] : null;
};

const StatCard = ({ icon, title, value, colorClass }) => (
    <div className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-md flex items-center space-x-4">
        <div className={`p-3 rounded-full ${colorClass}`}>{icon}</div>
        <div>
            <p className="text-sm text-gray-500 dark:text-neutral-400">{title}</p>
            <p className="text-2xl font-bold text-gray-800 dark:text-white">{value}</p>
        </div>
    </div>
);

const ProfileSection = ({ idContact }) => {

    const { currentEvent } = useContext(EventContext);
    const [contact, setContact] = useState(null);



    const getContact = async (id) => {

        await fetch(`${API_URL}?action=getContact&id_contact=${idContact}`)
            .then(response => response.json())
            .then(data => {
                // Handle the fetched contact data
                setContact(data);
            });
    };

    const { partenaireList } = useGetPartenaires({ params: `AND c.id_contact=${idContact} AND cf.id_event=${currentEvent?.id_event}  ` })

    useEffect(() => {
        if (idContact) {
            getContact(idContact);
        }
    }, [idContact]);


    if (!contact) return null;

    return (
        <div className="bg-white dark:bg-neutral-900 p-4 rounded-xl shadow-md mb-8 flex items-center space-x-6">

            <img
                src={contact.photos.large}
                alt={`Profil de ${contact.prenom} ${contact.nom}`}
                className="w-20 h-20 rounded-full object-cover border-4 border-gray-100 dark:border-neutral-700"
            />
            <div>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">{contact.prenom} {contact.nom} - {contact.societe}</h2>

                <div className="flex items-center space-x-4 mt-2">
                    {contact.mail && <span className="text-sm text-gray-500 dark:text-neutral-400">Email : <a href={`mailto:${contact.mail}`} className="text-e2o-600 hover:underline">{contact.mail}</a></span>}
                    {contact.port && <span className="text-sm text-gray-500 dark:text-neutral-400">Mobile : {contact.port}</span>}
                </div>
                {partenaireList && partenaireList.length > 0 && (
                    <div className="flex items-center space-x-2 mt-2">
                        {partenaireList.map((partenaire, index) => (
                            <div key={index} className="rounded-full px-4 py-2 flex items-center justify-center bg-gray-200 dark:bg-neutral-800 text-gray-600 dark:text-neutral-300 text-xs font-medium mb-1">
                                {partenaire.conferencier_statut?.libelle}
                            </div>
                        ))}
                    </div>
                )}
            </div>

        </div>
    )

}

const RegistrationChart = ({ data }) => (
    <div className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-md">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Inscriptions par jour</h2>
        <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
                <BarChart data={data} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip cursor={{ fill: 'rgba(239, 246, 255, 0.7)' }} />
                    <Legend wrapperStyle={{ fontSize: '14px' }} />
                    <Bar dataKey="inscriptions" fill="#3b82f6" name="Inscriptions" />
                </BarChart>
            </ResponsiveContainer>
        </div>
    </div>
);



const RegistrationItem = ({ registration, slot }) => (
    <li className="py-3 px-4 hover:bg-gray-50 dark:hover:bg-neutral-800 rounded-lg border-b dark:border-neutral-700 last:border-b-0 transition-colors">
        <div className="flex items-center justify-between">
            <div>
                <p className="text-gray-800 dark:text-white text-sm">{registration.contact.prenom} {registration.contact.nom}</p>
                <p className="font-semibold  text-gray-600 dark:text-neutral-300">{registration.contact.societe}</p>
            </div>
            <div className="text-right">
                <p className="text-sm text-gray-500 dark:text-neutral-400">Inscrit le</p>
                <p className="text-sm font-medium text-gray-700 dark:text-neutral-200">{moment(registration.date_creation).format('DD/MM/YYYY')}</p>
            </div>
        </div>


        {slot && (
            <div className="mt-2 bg-e2o-50 dark:bg-e2o-900/30 p-2 rounded-md text-sm text-e2o-800 dark:text-e2o-300">
                Créneau: <span className="font-semibold">{moment(slot.conf_event_date).format('ddd D MMM')} à {slot.heure_debut_local}</span>
            </div>
        )}
    </li>
);

const TimeSlot = ({ slot, isAdmin, viewType, formulaire, profile }) => {

    const { currentEvent } = useContext(EventContext);

    const { isBooked, conferencier } = slot;

    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [isVideoUrlCopied, setIsVideoUrlCopied] = useState(false);
    const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);


    if (!isBooked) {


        if (viewType === "liste") {
            return (
                <tr className=" text-gray-500 dark:text-neutral-400 mt-2 bg-green-50 dark:bg-green-900/20 border-t dark:border-neutral-700">
                    <td>{slot.heure_debut} - {slot.heure_fin}</td>
                    <td className="px-4 py-2"><span className="text-xs font-bold text-green-600 mt-1">Disponible</span></td>
                    <td className="px-4 py-2">
                        <div className="text-xs rounded px-2 py-1 text-white w-fit" style={{
                            backgroundColor: `${slot.type.color}`
                        }}>{slot.type.conf_event_type_nom}</div>
                        {slot.cel_titre}
                    </td>
                </tr>
            );
        }

        return (
            <div className="p-3 rounded-lg border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 flex flex-col justify-center items-center h-full text-center hover:shadow-md transition-shadow">
                <span className="font-mono font-semibold text-gray-700 dark:text-neutral-200">{slot.heure_debut_local}</span>
                <span className="  text-xs text-gray-400 dark:text-neutral-500">Heure Paris : {slot.heure_debut}</span>
                <span className="text-xs font-bold text-green-600 mt-1">DISPONIBLE</span>
                {isAdmin ? <span className="text-xs text-gray-500 dark:text-neutral-400">ID: {slot.id_conf_event}</span> : null}
            </div>
        );
    }



    if (viewType === "liste" && isBooked) {

        let fiche_production_mode = '';

        switch (conferencier.id_formulaire) {

            case '105': fiche_production_mode = 'game_changer'; break;
            case '154': fiche_production_mode = 'intervenant'; break;
            default: fiche_production_mode = 'intervenant'; break;
        }

        const idFormulaire = formulaire?.id_formulaire || conferencier.id_formulaire || '105';
        const route = formulaire?.route || 'enregistrement-webcast';
        const formUrl = `/events/${currentEvent?.id_event}/signup/${route}/c/${conferencier.contact.id_contact}?form-name=${route}&formisvisible=1&id_formulaire=${idFormulaire}`;
        const formUrlStep2 = `${formUrl}&fiche_production_mode=${fiche_production_mode}&step=2`;


        const qrData = encodeURIComponent(`https://www.myglobalvillage.com/events/${currentEvent?.id_event}/signup/${route}/c/${conferencier.contact.id_contact}?form-name=${route}&formisvisible=1&id_formulaire=${idFormulaire}&fiche_production_mode=${fiche_production_mode}&step=2`);
        const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=1000x1000&data=${qrData}`;

        // Vidéo "produite" (publiée sur YouTube via le pipeline de montage)
        const video = slot.video;
        const producedYoutubeId = video?.id_video?.id_youtube || (video?.video_url ? extractYoutubeId(video.video_url) : null);

        // Fallback : fichier brut uploadé par l'intervenant (pas encore monté/publié)
        const rawVideoUrl = conferencier.presta?.video_url ||
            (conferencier.presta?.video_hosted ? `https://www.mlg-consulting.com/manager_cc/docs/archives/${conferencier.presta.video_hosted}` : null);
        const rawYoutubeId = rawVideoUrl ? extractYoutubeId(rawVideoUrl) : null;

        const youtubeId = producedYoutubeId || rawYoutubeId;
        const videoThumbnail = video?.id_video?.thumbnail_large_url || video?.id_video?.thumbnail_medium_url
            || (youtubeId ? `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg` : null);
        const hasVideo = !!(youtubeId || rawVideoUrl || video?.id_video?.embed_url);
        const rawVideoOnly = !youtubeId && rawVideoUrl;
        const videoUrlToCopy = youtubeId ? `https://youtu.be/${youtubeId}` : (rawVideoUrl || video?.id_video?.embed_url || '');
        const videoDownloadUrl = rawVideoUrl || null; // téléchargement direct uniquement pour le fichier brut hébergé (pas de source fichier pour une vidéo YouTube)

        const handleDownloadVideo = async (e) => {
            e.stopPropagation();
            if (!videoDownloadUrl || isDownloadingVideo) return;
            setIsDownloadingVideo(true);
            try {
                // Récupère le fichier en mémoire pour forcer une véritable boîte de dialogue
                // d'enregistrement plutôt que la lecture inline habituelle du navigateur.
                const res = await fetch(videoDownloadUrl);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const blob = await res.blob();
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = videoDownloadUrl.split('/').pop() || 'video.mp4';
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(blobUrl);
            } catch (err) {
                // Le fichier n'est pas accessible en CORS depuis ce domaine : on retombe
                // sur l'ouverture directe (comportement précédent) plutôt que de bloquer l'utilisateur.
                console.error('Téléchargement direct impossible, ouverture du fichier :', err);
                window.open(videoDownloadUrl, '_blank', 'noopener,noreferrer');
            } finally {
                setIsDownloadingVideo(false);
            }
        };

        const copyVideoUrl = () => {
            if (!videoUrlToCopy) return;
            navigator.clipboard.writeText(videoUrlToCopy);
            setIsVideoUrlCopied(true);
            setTimeout(() => setIsVideoUrlCopied(false), 2000);
        };


        return (
            <>
                <tr className=" text-gray-700 dark:text-neutral-200 mt-2 bg-e2o-50 dark:bg-e2o-900/20 border-t dark:border-neutral-700">
                    <td>{slot.heure_debut} - {slot.heure_fin}</td>
                    <td>{slot.type.conf_event_type_nom}

                        <div className="mt-2 flex-2">
                            <a href={`/events/${currentEvent?.id_event}/signup/enregistrement-webcast/c/${conferencier.contact.id_contact}?form-name=${formulaire?.route}&formisvisible=1&id_formulaire=${formulaire?.id_formulaire || '105'}&fiche_production_mode=${fiche_production_mode}&step=2`}
                                className=" inline-block text-e2o-600 hover:underline text-xs font-medium"
                                target="_blank" rel="noopener noreferrer">
                                Vérifications / page 2
                                <ExternalLink className="inline-block w-3 h-3 ml-1" />
                                <span className="text-xs text-gray-500">(form id: {formulaire?.id_formulaire || '105'})</span>
                            </a>

                            <div className="flex items-center mt-2">
                                <img
                                    src={qrSrc}
                                    alt="QR Code"
                                    className="w-10 h-10 cursor-pointer"
                                    onClick={() => setIsQrModalOpen(true)}
                                />
                                {hasVideo ? (
                                    <button
                                        type="button"
                                        onClick={() => setIsVideoModalOpen(true)}
                                        className="relative w-14 h-10 ml-2 rounded overflow-hidden border dark:border-neutral-700 group shrink-0 bg-neutral-800"
                                        title="Lancer la vidéo en plein écran"
                                    >
                                        {videoThumbnail ? (
                                            <img src={videoThumbnail} alt="Miniature vidéo" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center" title="Vidéo brute non montée">
                                                <Film className="w-4 h-4 text-neutral-400" />
                                            </div>
                                        )}
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                                            <PlayCircle className="w-5 h-5 text-white" />
                                        </span>
                                    </button>
                                ) : (
                                    <div
                                        className="w-14 h-10 ml-2 rounded border border-dashed dark:border-neutral-700 flex items-center justify-center shrink-0"
                                        title="Aucune vidéo disponible"
                                    >
                                        <Film className="w-4 h-4 text-gray-300 dark:text-neutral-600" />
                                    </div>
                                )}
                                <img src={conferencier.contact.logos?.small} alt={`${conferencier.contact.prenom} ${conferencier.contact.nom}`} className="w-6 h-6 rounded-full inline-block ml-2" />
                            </div>
                        </div>


                    </td>
                    <td className="px-4 py-2">
                        <div>{conferencier.contact.prenom} {conferencier.contact.nom}</div>
                        <div className="font-semibold  text-gray-600 dark:text-neutral-300">{conferencier.contact.societe}</div>
                        <div className="text-xs text-gray-500 dark:text-neutral-400">E-mail : {conferencier.contact.mail}</div>
                        <div className="text-xs text-gray-500 dark:text-neutral-400">Tél : {conferencier.contact.port}</div>

                        <button
                            type="button"
                            onClick={() => setShowShareModal(true)}
                            className="inline-flex items-center gap-1 text-e2o-600 hover:underline text-xs font-medium"
                        >
                            <Share2 className="inline-block w-3 h-3" />
                            Partager le lien de mise à jour
                        </button>
                        {showShareModal && (
                            <SendSlotLinkModal
                                currentEvent={currentEvent}
                                onClose={() => setShowShareModal(false)}
                                profile={profile}
                                initialContact={conferencier.contact}
                                initialConferencierId={conferencier.id_conferencier}
                            />
                        )}

                    </td>
                </tr>
                {isQrModalOpen && (
                    <div
                        className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
                        onClick={() => setIsQrModalOpen(false)}
                    >
                        <button
                            type="button"
                            className="absolute top-4 right-4 text-white text-3xl leading-none"
                            onClick={() => setIsQrModalOpen(false)}
                            aria-label="Fermer la modal QR Code"
                        >
                            ×
                        </button>

                        <div
                            className="flex flex-col items-center gap-3"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={qrSrc}
                                alt="QR Code plein écran"
                                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                            />
                            <a
                                href={formUrlStep2}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white text-sm underline underline-offset-2"
                            >
                                Ouvrir le lien associé au QR
                            </a>
                        </div>
                    </div>
                )}
                {isVideoModalOpen && (
                    <div
                        className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
                        onClick={() => setIsVideoModalOpen(false)}
                    >
                        <div className="absolute top-4 right-4 flex items-center gap-2">
                            {videoDownloadUrl && (
                                <button
                                    type="button"
                                    onClick={handleDownloadVideo}
                                    disabled={isDownloadingVideo}
                                    title="Télécharger la vidéo"
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-neutral-200 hover:text-white bg-white/10 hover:bg-white/20 transition disabled:opacity-50"
                                >
                                    <Download className="w-4 h-4" /> {isDownloadingVideo ? 'Téléchargement…' : 'Télécharger'}
                                </button>
                            )}
                            {videoUrlToCopy && (
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); copyVideoUrl(); }}
                                    title="Copier l'URL de la vidéo"
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-neutral-200 hover:text-white bg-white/10 hover:bg-white/20 transition"
                                >
                                    {isVideoUrlCopied
                                        ? <><Check className="w-4 h-4 text-emerald-400" /> Copié !</>
                                        : <><Copy className="w-4 h-4" /> Copier l'URL</>
                                    }
                                </button>
                            )}
                            <button
                                type="button"
                                className="text-white text-3xl leading-none"
                                onClick={() => setIsVideoModalOpen(false)}
                                aria-label="Fermer la vidéo"
                            >
                                ×
                            </button>
                        </div>

                        <div
                            className="w-full max-w-4xl aspect-video"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {youtubeId ? (
                                <iframe
                                    src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                                    title="Vidéo"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="w-full h-full rounded-lg"
                                />
                            ) : rawVideoOnly ? (
                                <video
                                    src={rawVideoUrl}
                                    controls
                                    autoPlay
                                    className="w-full h-full rounded-lg bg-black"
                                />
                            ) : video?.id_video?.embed_url ? (
                                <iframe
                                    src={video.id_video.embed_url}
                                    title="Vidéo"
                                    frameBorder="0"
                                    allow="autoplay; fullscreen"
                                    allowFullScreen
                                    className="w-full h-full rounded-lg"
                                />
                            ) : null}
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <div className="p-3 rounded-lg border bg-e2o-50 dark:bg-e2o-900/20 border-e2o-300 dark:border-e2o-800 flex flex-col h-full text-left hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-2">
                <div>
                    <div className="font-mono text-base font-bold text-e2o-700 dark:text-e2o-400">{slot.heure_debut_local}</div>
                    <div className="  text-xs text-gray-400 dark:text-neutral-500">Heure Paris : {slot.heure_debut}</div>
                </div>
                {conferencier?.contact && (
                    <div className="text-right">
                        <p className="text-xs text-gray-900 dark:text-white">{conferencier.contact.prenom} {conferencier.contact.nom}</p>
                        <p className=" text-gray-600 dark:text-neutral-300 font-semibold  text-sm">{conferencier.contact.societe}</p>
                    </div>
                )}

            </div>
            {conferencier?.contact && (
                <div className="border-t dark:border-neutral-700 mt-auto pt-2 text-xs text-gray-500 dark:text-neutral-400 space-y-1">
                    {conferencier.contact.mail && <div><span className="font-medium">Email:</span> <a href={`mailto:${conferencier.contact.mail}`} className="text-e2o-600 hover:underline">{conferencier.contact.mail}</a></div>}
                    {conferencier.contact.port && <div><span className="font-medium">Tél:</span> {conferencier.contact.port}</div>}

                    <div className="mt-2 border-t dark:border-neutral-700 pt-1">
                        <a target="_blank" rel="noreferrer" className="text-e2o-600 hover:underline" href={`/events/${currentEvent?.id_event}/signup/enregistrement-webcast/c/${conferencier.contact.id_contact}?form-name=enregistrement-webcast&formisvisible=1&id_formulaire=105`}>Afficher le formulaire</a>
                    </div>
                </div>
            )}

            {isAdmin && <div className="text-xs text-gray-400 dark:text-neutral-500 mt-2 border-t dark:border-neutral-700 pt-1    ">
                id_contact : {conferencier.contact.id_contact}
                <br />
                id_conferencier : {conferencier.id_conferencier}
                <br />
                id_conf_event : {slot.id_conf_event}
            </div>
            }

        </div>
    );
};

const RegistrationDashbord = ({ isAdmin = false, profile }) => {
    const { t, i18n, apiUrlTranslation } = useApiUrlTranslation();
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { currentEvent } = useContext(EventContext);
    const scheduleRefs = useRef({});

    const [viewType, setViewType] = useState('liste'); // 'tableau' ou 'liste'
    const [hidePastSlots, setHidePastSlots] = useState(true);

    const history = useHistory(); // Utiliser le hook useHistory

    const { ownerIdContact } = useParams();
    const [formulaireMap, setFormulaireMap] = useState({});


    const handleChangeView = (viewType) => {
        // Implémentez la logique pour changer la vue ici
        setViewType(viewType);
        console.log(`Changement de vue vers : ${viewType}`);
    }

    const getFormulaireList = async ({ idFormulaireList }) => {
        const ids = idFormulaireList
            .filter(Boolean)
            .filter((id, index, self) => self.indexOf(id) === index)
            .filter(id => !['0', 0, 'undefined', undefined, '[object Object]'].includes(id));

        if (!ids.length) return [];
        try {
            const res = await fetch(`${apiUrlTranslation}?action=getFormulaireList&params= WHERE id_formulaire IN (${ids.join(',')})`);
            return await res.json();
        } catch (e) {
            console.error('getFormulaireList error:', e);
            return [];
        }
    }


    useEffect(() => {
        moment.locale(i18n.resolvedLanguage || 'fr');
    }, [i18n.resolvedLanguage]);

    useEffect(() => {
        if (!currentEvent?.id_event) return;


        const controller = new AbortController();
        const signal = controller.signal;


        const fetchAndProcessData = async () => {

            const filterSuite = ownerIdContact ? ` AND owner_id_contact=${ownerIdContact}` : '';

            try {
                setLoading(true);
                const [partenairesRes, confEventRes] = await Promise.all([
                    fetch(`${apiUrlTranslation}?action=getPartenairesLight&params=AND cf.id_event=${currentEvent.id_event} AND cf.statut=143`),
                    fetch(`${apiUrlTranslation}?action=getConfeventLight&id_event=${currentEvent.id_event}&filter=and publier!='n' ${filterSuite}`),
                ]);

                if (signal.aborted) return;


                if (!partenairesRes.ok || !confEventRes.ok) throw new Error('Erreur réseau lors de la récupération des données');

                const safeJson = async (res) => {
                    const text = await res.text();
                    if (!text || !text.trim()) return [];
                    return JSON.parse(text);
                };

                const partenaires = await safeJson(partenairesRes);
                const allSlots = await safeJson(confEventRes);

                const bookingsMap = new Map(partenaires.map(p => [parseInt(p.id_conf_event, 10), p]));
                const slotsMap = new Map(allSlots.map(s => [parseInt(s.id_conf_event, 10), s]));

                const bookedConfEventIds = partenaires.map(p => parseInt(p.id_conf_event, 10)).filter(Boolean);
                let videosMap = new Map();
                if (bookedConfEventIds.length > 0) {
                    try {
                        const videosFilter = `WHERE vc.id_conf_event IN (${bookedConfEventIds.join(',')})`;
                        const videosRes = await fetch(`${apiUrlTranslation}?action=getVideosConferences&filter=${encodeURIComponent(videosFilter)}`);
                        const videos = await safeJson(videosRes);
                        videosMap = new Map(videos.map(v => [String(v.id_conf_event?.id_conf_event ?? v.id_conf_event), v]));
                    } catch (e) {
                        console.error('getVideosConferences error:', e);
                    }
                }

                const enrichedSlots = allSlots.map(slot => {
                    const bookingInfo = bookingsMap.get(parseInt(slot.id_conf_event, 10));
                    const video = videosMap.get(String(slot.id_conf_event)) || null;
                    return { ...slot, isBooked: !!bookingInfo, conferencier: bookingInfo || null, video };
                });

                const schedule = enrichedSlots.reduce((acc, slot) => {
                    const date = slot.conf_event_date;
                    if (!acc[date]) acc[date] = [];
                    acc[date].push(slot);
                    return acc;
                }, {});

                for (const date in schedule) {
                    schedule[date].sort((a, b) => a.heure_debut_local.localeCompare(b.heure_debut_local));
                }

                // Trie les journées par ordre chronologique (l'ordre d'insertion dépend de l'API, pas de la date)
                const sortedSchedule = Object.keys(schedule)
                    .sort()
                    .reduce((acc, date) => {
                        acc[date] = schedule[date];
                        return acc;
                    }, {});

                const registrationsByDay = partenaires.reduce((acc, reg) => {
                    const date = moment(reg.date_creation).format('YYYY-MM-DD');
                    acc[date] = (acc[date] || 0) + 1;
                    return acc;
                }, {});


                const chartData = Object.keys(registrationsByDay)
                    .sort()
                    .map(date => ({
                        name: moment(date).format('DD/MM'),
                        inscriptions: registrationsByDay[date],
                    }));


                const formulaires = await getFormulaireList({
                    idFormulaireList: partenaires.map(p => p.id_formulaire),
                });
                const fMap = Object.fromEntries(formulaires.map(f => [String(f.id_formulaire), f]));
                setFormulaireMap(fMap);

                setDashboardData({
                    stats: {
                        total: partenaires.length,
                        booked: bookingsMap.size,
                        available: allSlots.length - bookingsMap.size,
                    },
                    recentRegistrations: [...partenaires].sort((a, b) => new Date(b.date_creation) - new Date(a.date_creation)).slice(0, 5),
                    schedule: sortedSchedule,
                    slotsMap: slotsMap,
                    chartData: chartData,
                });

            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError(err.message);
                    console.error("Erreur lors de la construction du dashboard:", err);
                }
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchAndProcessData();

        return () => {
            controller.abort();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEvent?.id_event]);

    useEffect(() => {
        if (dashboardData?.schedule && Object.keys(dashboardData.schedule).length > 0) {
            const today = moment().format('YYYY-MM-DD');
            const sortedDates = Object.keys(dashboardData.schedule).sort();
            const targetDate = sortedDates.find(date => date >= today);

            // if (targetDate) {
            //     const anchorId = `schedule-${targetDate}`;
            //     // Met à jour l'URL avec l'ancre
            //     history.replace(`${history.location.pathname}#${anchorId}`);

            //     // Force le défilement vers l'ancre pour assurer le fonctionnement au premier chargement
            //     setTimeout(() => {
            //         const element = document.getElementById(anchorId);
            //         if (element) {
            //             element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            //         }
            //     }, 100); // Léger délai pour laisser le temps au DOM de se stabiliser
            // }
        }
    }, [dashboardData, history]);



    if (loading) return <Layout className="bg-white dark:bg-neutral-950"><div className="text-center p-10 m-10"><Loader className="" />  </div></Layout>;
    if (error) return <Layout><div className="text-center p-10 text-red-500">Erreur: {error}</div></Layout>;

    const {
        stats = { total: 0, booked: 0, available: 0 },
        recentRegistrations = [],
        schedule: fullSchedule = {},
        slotsMap = new Map(),
        chartData = [] // Initialisation des données du graphique
    } = dashboardData || {};

    const pastGraceLimit = moment().subtract(3, 'hours');
    const schedule = hidePastSlots
        ? Object.entries(fullSchedule).reduce((acc, [date, slots]) => {
            const upcomingSlots = slots.filter(slot => moment(`${slot.conf_event_date} ${slot.heure_fin}`).isSameOrAfter(pastGraceLimit));
            if (upcomingSlots.length > 0) acc[date] = upcomingSlots;
            return acc;
        }, {})
        : fullSchedule;

    return (
        <Layout>

            {profile}
            <div className="p-8 bg-gray-50 dark:bg-neutral-950 min-h-screen">
                <header className="mb-8">

                </header>

                <ProfileSection idContact={ownerIdContact} />

                <RegistrationDashbordNav
                    isAdmin={isAdmin}
                    profile={profile} />

                <div className="hidden flex flex-wrap items-center gap-x-8 gap-y-3 p-4 bg-white rounded-xl shadow-md mb-6">

                    <Link
                        target="_blank"
                        rel="noreferrer"
                        to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/registration-dashboard-coordinateur/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/registration-dashboard/`}`}
                        className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                        <Users className="h-5 w-5 mr-2 text-gray-400" />
                        <span>Accueil</span>
                    </Link>

                    {
                        //&id_cycle_lang=3068
                    }
                    <a
                        target="_blank"
                        rel="noreferrer"
                        href={`https://www.event2one.com/screen_manager/content/contact/?&content=fiche_production&target=obs&id_event_ligne=${currentEvent?.event_ligne.id_event_ligne}&id_contact=227438`}
                        className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                        <Users className="h-5 w-5 mr-2 text-gray-400" />
                        <span>Fiche de production</span>
                    </a>

                    {profile !== "coordinateur" && (
                        <Link
                            target="_blank"
                            rel="noreferrer"
                            to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/registration-dashboard-forms/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/registration-dashboard-forms/`}`}
                            className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                            <Camera className="h-5 w-5 mr-2 text-gray-400" />
                            <span>Bibliothèque de formulaires</span>
                        </Link>
                    )}

                    {profile === "coordinateur" && (
                        <Link
                            target="_blank"
                            rel="noreferrer"
                            to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/registration-dashboard-links/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/registration-dashboard-links/`}`}
                            className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                            <Camera className="h-5 w-5 mr-2 text-gray-400" />
                            <span>Bibliothèque de formulaires</span>
                        </Link>
                    )}

                    {/* <Link
                        target="_blank"
                        rel="noreferrer"
                        to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/registration-dashboard-details/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/registration-dashboard-details/`}`}
                        className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                        <Users className="h-5 w-5 mr-2 text-gray-400" />
                        <span>Inscriptions</span>
                    </Link> */}

                    <Link
                        target="_blank"
                        rel="noreferrer"
                        to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/partenaires-supports/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/partenaires-supports/`}`}
                        className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                        <FileCheck className="h-5 w-5 mr-2 text-gray-400" />
                        <span>Vérifier les supports</span>
                    </Link>

                    {isAdmin && profile !== "coordinateur" && (
                        <>
                            <Link
                                target="_blank"
                                rel="noreferrer"
                                to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/registration-dashboard-captations-admin/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/registration-dashboard-captations-admin`}`}
                                className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                                <Camera className="h-5 w-5 mr-2 text-gray-400" />
                                <span>Chargement des vidéos</span>
                            </Link>
                            <a
                                rel="noreferrer"
                                target="_blank"
                                href={`https://www.event2one.com:3000/event/${currentEvent?.id_event}/admin/0`}
                                className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                                <Clapperboard className="h-5 w-5 mr-2 text-gray-400" />
                                <span>Broadcast</span>
                            </a>
                            <Link
                                target="_blank"
                                rel="noreferrer"
                                to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/post-generator/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/post-generator`}`}
                                className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                                <Camera className="h-5 w-5 mr-2 text-gray-400" />
                                <span>Generateur de post</span>
                            </Link>
                            <Link
                                target="_blank"
                                rel="noreferrer"
                                to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/registration-dashboard-captations/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/registration-dashboard-captations`}`}
                                className="flex items-center text-gray-700 hover:text-e2o-600 font-medium transition-colors">
                                <Camera className="h-5 w-5 mr-2 text-gray-400" />
                                <span>Portail vidéos</span>
                            </Link>


                        </>

                    )}


                </div>

                <p className="text-gray-600 mt-1 my-5 hidden"> Suivez les inscriptions et gérez les créneaux de rendez-vous</p>


                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <StatCard
                        icon={<Users className="h-6 w-6 text-e2o-600" />}
                        title="Total Inscriptions"
                        value={stats.total}
                        colorClass="bg-e2o-100" />
                    <StatCard icon={<CalendarCheck className="h-6 w-6 text-green-600" />} title="Créneaux Réservés" value={stats.booked} colorClass="bg-green-100" />
                    <StatCard icon={<CalendarX className="h-6 w-6 text-orange-600" />} title="Créneaux Disponibles" value={stats.available} colorClass="bg-orange-100" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    <div className="lg:col-span-1 bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-md">
                        <div className="mb-8">
                            <RegistrationChart data={chartData} />
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Dernières Inscriptions</h2>
                        <Link to={`${ownerIdContact ? `/events/${currentEvent?.id_event}/registration-dashboard-details/c/${ownerIdContact}` : `/events/${currentEvent?.id_event}/registration-dashboard-details/`}`} className="text-sm font-medium text-e2o-600 hover:underline flex items-center">
                            <span>Voir la liste</span>
                            <ChevronRight className="h-4 w-4 ml-1" />
                        </Link>
                        <ul className="space-y-2">
                            {recentRegistrations.length > 0 ? (
                                recentRegistrations.map(reg => {
                                    const confEventId = reg.id_conf_event ? parseInt(reg.id_conf_event, 10) : null;
                                    const slot = confEventId ? slotsMap.get(confEventId) : null;
                                    // Correction: Utiliser une clé unique combinant l'ID du contact et l'ID du conférencier
                                    return <RegistrationItem key={`${reg.id_contact}-${reg.id_conferencier}`} registration={reg} slot={slot} />;
                                })
                            ) : (
                                <p className="text-gray-500 dark:text-neutral-400">Aucune inscription récente.</p>
                            )}
                        </ul>
                    </div>

                    <div className="lg:col-span-2 bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-md">
                        <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Planning des Rendez-vous</h2>

                        <div className="flex gap-2 mb-4 items-center">
                            <button
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${viewType === 'tableau' ? 'bg-gray-600 text-white' : 'bg-gray-200 dark:bg-neutral-700 text-gray-700 dark:text-neutral-200 hover:bg-gray-300 dark:hover:bg-neutral-600'}`}
                                onClick={() => handleChangeView('tableau')}>
                                <LayoutGrid className="h-4 w-4" />
                                <span>Tableau</span>
                            </button>
                            <button
                                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${viewType === 'liste' ? 'bg-gray-600 text-white' : 'bg-gray-200 dark:bg-neutral-700 text-gray-700 dark:text-neutral-200 hover:bg-gray-300 dark:hover:bg-neutral-600'}`}
                                onClick={() => handleChangeView('liste')}>
                                <List className="h-4 w-4" />
                                <span>Liste</span>
                            </button>
                            <label className="flex items-center gap-2 ml-2 text-sm font-medium text-gray-700 dark:text-neutral-200 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={hidePastSlots}
                                    onChange={(e) => setHidePastSlots(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-e2o-600 focus:ring-e2o-500"
                                />
                                <span>Masquer les créneaux passés</span>
                            </label>
                        </div>

                        {Object.keys(schedule).length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4 border-b dark:border-neutral-700 pb-4 ">
                                {Object.keys(schedule).map(date => (
                                    <a
                                        key={date}
                                        href={`#schedule-${date}`}
                                        className="px-3 py-1 text-sm font-medium text-gray-600 dark:text-neutral-300 bg-gray-100 dark:bg-neutral-800 rounded-full hover:bg-e2o-100 dark:hover:bg-e2o-900/30 hover:text-e2o-700 dark:hover:text-e2o-400 transition-colors"
                                    >
                                        {moment(date).format('ddd D MMM')}
                                    </a>
                                ))}
                            </div>
                        )}


                        {viewType === 'liste' &&
                            <div className="space-y-6   pr-3">
                                {Object.keys(schedule).length > 0 ? (
                                    Object.entries(schedule).map(([date, slots]) => (
                                        <div
                                            key={date}
                                            id={`schedule-${date}`}
                                            className="" // Le navigateur défilera vers cet ID
                                        >
                                            <h3 className="font-semibold text-gray-700 dark:text-neutral-200 mb-3 sticky top-0 bg-white dark:bg-neutral-900 py-2 border-b dark:border-neutral-700 shadow-sm z-10">{moment(date).format('dddd D MMMM YYYY')}</h3>
                                            <table className=" w-full table-auto border-collapse">
                                                <tbody>
                                                    {slots.map((slot, idx) => {
                                                        const prevSlot = slots[idx - 1];
                                                        const showTitleSeparator = !prevSlot || prevSlot.cel_titre !== slot.cel_titre;

                                                        let groupEndTime = slot.heure_fin;
                                                        if (showTitleSeparator) {
                                                            // Trouver le dernier slot du groupe pour obtenir l'heure de fin
                                                            for (let i = idx; i < slots.length; i++) {
                                                                if (slots[i].cel_titre === slot.cel_titre) {
                                                                    groupEndTime = slots[i].heure_fin;
                                                                } else {
                                                                    break;
                                                                }
                                                            }
                                                        }

                                                        return (
                                                            <React.Fragment key={slot.id_conf_event}>
                                                                {showTitleSeparator && (
                                                                    <tr className="bg-gray-600">
                                                                        <td colSpan={3} className="px-4 py-2 text-sm font-bold text-white border-y border-gray-200 dark:border-neutral-600">
                                                                            <div className="flex items-center space-x-4">
                                                                                <span className=" font-bold text-white">{slot.heure_debut} - {groupEndTime}</span>
                                                                                <span>{slot.cel_titre}</span>

                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                <TimeSlot
                                                                    viewType={viewType}
                                                                    slot={slot}
                                                                    isAdmin={isAdmin}
                                                                    formulaire={formulaireMap[String(slot.conferencier?.id_formulaire)]}
                                                                    profile={profile}
                                                                />
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-gray-500 dark:text-neutral-400">Aucun créneau programmé.</p>
                                )}
                            </div>
                        }

                        {viewType === 'tableau' && <div className="space-y-6 max-h-[600px] overflow-y-auto pr-3">
                            {Object.keys(schedule).length > 0 ? (
                                Object.entries(schedule).map(([date, slots]) => (
                                    <div
                                        key={date}
                                        id={`schedule-${date}`}
                                        className="pt-2" // Le navigateur défilera vers cet ID
                                    >
                                        <h3 className="font-semibold text-gray-700 mb-3 sticky top-0 bg-white py-2 border-b">{moment(date).format('dddd D MMMM YYYY')}</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {slots.map(slot => <TimeSlot
                                                viewType={viewType}
                                                key={slot.id_conf_event}
                                                slot={slot}
                                                isAdmin={isAdmin}
                                                formulaire={formulaireMap[String(slot.conferencier?.id_formulaire)]}
                                                profile={profile}
                                            />)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500">Aucun créneau programmé.</p>
                            )}
                        </div>}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default RegistrationDashbord;